import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { QueryStatus } from '@reduxjs/toolkit/query';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { useUpdateDepositTransactionHashMutation } from '@/api/v1';
import { GeneralQuestionModal } from '@/components/molecules/GeneralQuestionModal';
import { LoadingButton } from '@/components/molecules/LoadingButton';
import { ApplicationModal } from '@/components/organisms/ApplicationModal';
import { useToast } from '@/hooks/useToast';
import { useTransactionId } from '@/hooks/useTransactionId';
import { useTranslator } from '@/hooks/useTranslator';

export const CryptoTxHashModal: React.FC = (): React.ReactElement => {
  const { pathname } = useLocation();

  // Derive paths from current URL - supports both /home/deposit and /customers/:id/deposit
  const basePath = React.useMemo((): string => {
    // Remove /hash from the end to get the base deposit path
    return pathname.replace(/\/hash$/, '');
  }, [pathname]);

  const closePath = React.useMemo((): string => {
    // For customer flow: /customers/:id/deposit -> /customers/:id
    // For home flow: /home/deposit -> /home
    return basePath.replace(/\/deposit$/, '');
  }, [basePath]);
  const toast = useToast();

  const [error, setError] = React.useState<string | null>(null);
  const [transactionHash, setTransactionHash] = React.useState<string>('');
  const [updateDepositTransactionHash, mutationResult] = useUpdateDepositTransactionHashMutation();
  const [exitConfirmationModalVisible, setExitConfirmationModalVisible] =
    React.useState<boolean>(false);

  const transactionId = useTransactionId();
  const tr = useTranslator();
  const navigate = useNavigate();

  const sending = React.useMemo((): boolean => {
    return mutationResult.status === QueryStatus.pending;
  }, [mutationResult.status]);

  const confirmClose = React.useCallback((): void => {
    navigate(closePath, { replace: true });
  }, [navigate, closePath]);

  const abortExit = React.useCallback((): void => {
    setExitConfirmationModalVisible(false);
  }, []);

  const onClose = React.useCallback((): void => {
    if (sending) {
      return;
    }
    setExitConfirmationModalVisible(true);
  }, [sending]);

  const onSubmit = React.useCallback(
    (event: React.FormEvent): void => {
      event.preventDefault();
      if (transactionHash.trim() === '') {
        setError(tr('VALIDATION_REQUIRED', tr('TRANSACTION_HASH')));
        return;
      }

      updateDepositTransactionHash({
        transactionId: transactionId,
        transactionHash: transactionHash,
      });
    },
    [transactionId, transactionHash, updateDepositTransactionHash, tr],
  );

  React.useEffect((): void => {
    if (mutationResult.status === QueryStatus.fulfilled) {
      navigate(`${basePath}/completed?crypto=true`, { replace: true });
    }
    if (mutationResult.status === QueryStatus.rejected) {
      toast.show(tr('AN_ERROR_OCCURRED'), 'error');
    }
  }, [navigate, basePath, mutationResult.status, toast, tr]);

  const onTransactionHashChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const { value } = event.target;
      setTransactionHash(value);
      setError(null);
    },
    [],
  );

  return (
    <ApplicationModal
      title={tr('CRYPTO_TRANSFER_MODAL_TITLE')}
      description={tr('CRYPTO_TRANSFER_MODAL_DESCRIPTION')}
      onClose={onClose}>
      <Box component="form" name="payment-method-fiat" onSubmit={onSubmit}>
        <TextField
          fullWidth={true}
          disabled={sending}
          name={uuidv4()}
          label={tr('TRANSACTION_HASH')}
          value={transactionHash}
          helperText={error}
          error={Boolean(error)}
          onChange={onTransactionHashChange}
        />
        <Box mt={5}>
          <LoadingButton type="submit" variant="contained" fullWidth={true} loading={sending}>
            {tr('SUBMIT')}
          </LoadingButton>
        </Box>
      </Box>

      <GeneralQuestionModal
        open={exitConfirmationModalVisible}
        title={tr('ARE_YOU_SURE_YOU_WANT_TO_LEAVE_WITHOUT_HASH')}
        description={tr('OPERATIONS_WITHOUT_A_TRANSACTION_HASH_TAKE_LONGER')}
        confirmButtonText={tr('SHARE_TRANSACTION_HASH')}
        abortButtonText={tr('LEAVE_ANYWAY')}
        onConfirm={abortExit}
        onAbort={confirmClose}
      />
    </ApplicationModal>
  );
};
